import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Tag, Loader2, Check, X, Sparkles, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency } from './CurrencyContext';
import CouponCelebration from './CouponCelebration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Inline coupon block that lives next to the bundle selector on the PDP.
 * - Renders an input + curiosity-driven CTA.
 * - Listens to `k:apply-prefill-coupon` (dispatched by MysteryModal) and to the
 *   `k_prefill_coupon` sessionStorage flag (set when arriving from a navigation).
 * - Calls /api/coupon/validate and notifies the parent with the resulting
 *   {code, percent_off} so the parent can recompute bundle prices.
 */
const CouponReveal = ({ onApplied, applied, bundles }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [celebrate, setCelebrate] = useState(null); // {code, percent_off}
  const rootRef = useRef(null);

  // Compute the discount preview for the currently displayed bundles
  const { formatPrice } = useCurrency();
  const fp = formatPrice || ((v) => `$${Number(v).toFixed(2)}`);

  const applyCode = async (incoming) => {
    const c = (incoming ?? code).trim().toUpperCase();
    if (!c) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/coupon/validate`, { code: c });
      onApplied?.(data);
      setCode(c);
      setCelebrate({ code: data.code || c, percent_off: data.percent_off });
      toast.success(`${c} applied · ${data.percent_off}% off`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Invalid coupon';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const remove = () => {
    onApplied?.(null);
    setCode('');
  };

  // Auto-fill + auto-scroll behavior coming from the welcome modal.
  useEffect(() => {
    const tryPrefill = () => {
      const stored = sessionStorage.getItem('k_prefill_coupon');
      if (stored) {
        setCode(stored);
        sessionStorage.removeItem('k_prefill_coupon');
        // Scroll into view smoothly so user sees the field
        if (rootRef.current) {
          rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        // Pulse focus on the apply button visually via setTimeout to ensure the
        // field is rendered before scroll.
        setTimeout(() => applyCode(stored), 700);
      }
    };
    tryPrefill();
    // Also react if the modal fires the event while the page is already open
    const handler = () => tryPrefill();
    window.addEventListener('k:apply-prefill-coupon', handler);
    // Hash deep-link support: #bundles -> scroll to this section
    if (window.location.hash === '#bundles' && rootRef.current) {
      rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    return () => window.removeEventListener('k:apply-prefill-coupon', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={rootRef} id="bundles" className="mb-5">
      <CouponCelebration
        open={!!celebrate}
        onClose={() => setCelebrate(null)}
        code={celebrate?.code}
        percentOff={celebrate?.percent_off}
      />
      {applied ? (
        <div
          data-testid="coupon-applied-banner"
          className="rounded-2xl p-4 relative overflow-hidden coupon-applied-glow"
          style={{
            background: 'linear-gradient(135deg, #2D5A3D 0%, #3F7A53 55%, #2D5A3D 100%)',
            boxShadow: '0 12px 30px rgba(45,90,61,0.28), 0 0 0 4px rgba(217,164,65,0.18)',
          }}
        >
          {/* Decorative sparkles */}
          <span className="coupon-sparkle coupon-sparkle-1" />
          <span className="coupon-sparkle coupon-sparkle-2" />
          <span className="coupon-sparkle coupon-sparkle-3" />

          <div className="flex items-center justify-between gap-3 mb-3 relative">
            <div className="flex items-center gap-2.5 min-w-0">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 coupon-medal-bounce"
                style={{
                  background: 'radial-gradient(circle at 30% 30%, #F0CE7A 0%, #D9A441 55%, #B0801F 100%)',
                  boxShadow: '0 4px 10px rgba(217,164,65,0.4)',
                }}
              >
                <PartyPopper size={18} color="#FFFFFF" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold flex items-center gap-1" style={{ color: '#F0CE7A' }}>
                  <Sparkles size={11} /> Discount unlocked
                </p>
                <p className="text-[15px] font-extrabold leading-tight truncate" style={{ color: '#FFFFFF' }}>
                  {applied.code} applied · {applied.percent_off}% OFF every bundle
                </p>
              </div>
            </div>
            <button
              onClick={remove}
              className="opacity-80 hover:opacity-100 shrink-0 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10"
              aria-label="Remove coupon"
              style={{ color: '#FFFFFF' }}
              data-testid="coupon-remove-btn"
            >
              <X size={16} />
            </button>
          </div>
          {/* Live preview of the discount applied to every bundle */}
          {bundles?.length > 0 && (
            <div className="grid grid-cols-3 gap-2 text-center relative">
              {bundles.map((b) => {
                const discounted = Number((b.price * (1 - applied.percent_off / 100)).toFixed(2));
                return (
                  <div
                    key={b.id}
                    className="rounded-xl p-2.5"
                    style={{ backgroundColor: 'rgba(255,255,255,0.96)' }}
                  >
                    <p className="text-[10px] uppercase tracking-wider opacity-70" style={{ color: '#2D5A3D' }}>
                      {b.bars}
                    </p>
                    <p className="text-[14px] font-extrabold mt-0.5" style={{ color: '#2D5A3D' }}>
                      {fp(discounted)}
                    </p>
                    <p className="text-[11px] line-through opacity-55" style={{ color: '#2C2418' }}>
                      {fp(b.price)}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div
          className="rounded-2xl p-4"
          style={{ backgroundColor: '#FFFFFF', border: '1px dashed rgba(45,90,61,0.4)' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} color="#2D5A3D" />
            <p className="text-[12px] uppercase tracking-[0.18em] font-semibold" style={{ color: '#2D5A3D' }}>
              Have a code?
            </p>
          </div>
          <div className="flex items-stretch gap-2">
            <div className="flex-1 flex items-center gap-2 px-3 rounded-lg border" style={{ borderColor: 'rgba(45,90,61,0.25)', backgroundColor: '#FAFAF7' }}>
              <Tag size={14} color="#2D5A3D" />
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === 'Enter') applyCode(); }}
                placeholder="WELCOME5"
                className="flex-1 bg-transparent py-3 text-[14px] focus:outline-none uppercase tracking-[0.12em] font-semibold placeholder:opacity-40 placeholder:font-normal"
                style={{ color: '#2C2418' }}
              />
            </div>
            <button
              onClick={() => applyCode()}
              disabled={!code.trim() || loading}
              className="px-5 rounded-lg text-[12px] uppercase tracking-wide font-bold btn-hover disabled:opacity-50"
              style={{ backgroundColor: '#2D5A3D', color: '#F5EDDF', minWidth: '170px' }}
            >
              {loading ? <Loader2 size={14} className="animate-spin mx-auto" /> : 'Reveal my new price'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponReveal;
